import { Menu } from "@/dto/menu";
import { GetItemDetailsResponse } from "@/dataLayer/getItemDetails";
import { getItemUrl } from "@/utils/itemUrlUtils";
import {
  getFromSessionStorage,
  removeFromSessionStorage,
} from "@/utils/sessionStorageUtils";
import { SessionStorageKey } from "@/utils/storageKeys";
import { OrderHistoryItem } from "@/types/orders";

export interface ReorderUnavailableItem {
  id: string;
  name: string;
  /** `/menu/<slug>` path for needsReAdd items; null for hardUnavailable items. */
  url: string | null;
}

export interface ReorderSessionData {
  unavailableItems: {
    needsReAdd: ReorderUnavailableItem[];
    hardUnavailable: ReorderUnavailableItem[];
  } | null;
  addedCount: number | null;
}

/**
 * Reads and clears the reorder session keys written by the order-again flow.
 * Call this when the cart hash indicates a reorder navigation (both success
 * and unavailable paths), passing whether the current hash is the unavailable
 * variant so the unavailable-items key is only consumed on that path.
 */
export function readAndClearReorderSession(
  isUnavailableHash: boolean
): ReorderSessionData {
  let unavailableItems: ReorderSessionData["unavailableItems"] = null;

  if (isUnavailableHash) {
    const stored = getFromSessionStorage<{
      needsReAdd: ReorderUnavailableItem[];
      hardUnavailable: ReorderUnavailableItem[];
    }>(SessionStorageKey.REORDER_UNAVAILABLE_ITEMS, null as any);
    if (stored) {
      unavailableItems = stored;
      removeFromSessionStorage(SessionStorageKey.REORDER_UNAVAILABLE_ITEMS);
    }
  }

  const countStored = getFromSessionStorage<{ count: number } | null>(
    SessionStorageKey.REORDER_ADDED_COUNT,
    null
  );
  const addedCount = countStored !== null ? countStored.count : null;
  if (countStored !== null) {
    removeFromSessionStorage(SessionStorageKey.REORDER_ADDED_COUNT);
  }

  return { unavailableItems, addedCount };
}

export interface ReorderValidationResult {
  valid: any[];
  needsReAdd: ReorderUnavailableItem[];
  hardUnavailable: ReorderUnavailableItem[];
}

/**
 * Returns the current catalogue entry for an order-history item. Both standard
 * retail items and variants are represented in getItemDetails.items, so an
 * exact ID match prevents eligibility from being copied from a sibling variant.
 */
export function getRetailReorderCatalogItem(
  historyItem: OrderHistoryItem,
  details: GetItemDetailsResponse | null
) {
  return details?.items?.find((item) => item.id === historyItem.id);
}

/**
 * Classifies each historic order item into one of three buckets:
 *
 * - valid          — item exists in menu AND all mandatory modifier groups are satisfied
 * - needsReAdd     — item exists but has new/changed mandatory modifier requirements
 * - hardUnavailable — item does not exist in the current menu at all
 *
 * Price changes do NOT affect classification.
 */
export function validateReorderItems(
  historyItems: any[],
  menu: Menu
): ReorderValidationResult {
  const valid: any[] = [];
  const needsReAdd: ReorderUnavailableItem[] = [];
  const hardUnavailable: ReorderUnavailableItem[] = [];

  for (const item of historyItems) {
    const menuItem = menu.items.find((i) => i.id === item.id);

    if (!menuItem || menuItem?.available === false) {
      hardUnavailable.push({ id: item.id, name: item.name, url: null });
      continue;
    }

    const structuralIssue = hasStructuralModifierIssue(item, menuItem, menu);

    if (structuralIssue) {
      needsReAdd.push({
        id: item.id,
        name: menuItem.name,
        url: `/menu/${getItemUrl(menuItem)}`,
      });
    } else {
      valid.push({
        ...item,
        name: menuItem.name,
        price: menuItem.price,
        isAgeRestricted: menuItem.isAgeRestricted,
        modifiers: getModifiers(item, menu),
      });
    }
  }

  return { valid, needsReAdd, hardUnavailable };
}

export function getModifiers(item: OrderHistoryItem, menu: Menu) {
  return (item.modifiers ?? []).map((modifier) => { 
    
    const menuModifier = menu?.modifiers?.find(
      (m) => m.id === modifier.id
    );

    if (!menuModifier) {
      return modifier;
    }

    return {
      ...modifier,
      name: menuModifier.name,
      price: menuModifier.price,
    };
  });
}

/**
 * Returns true when the historic item cannot be silently re-added because:
 * 1. A mandatory modifier group on the current menu item is not satisfied by the
 *    historic modifiers (new required group added, or not enough choices selected).
 * 2. A previously chosen modifier no longer exists in its current modifier group.
 */
function hasStructuralModifierIssue(
  historyItem: any,
  menuItem: any,
  menu: Menu
): boolean {
  const historicModifiers: any[] = historyItem.modifiers ?? [];

  // Check 1 — mandatory groups not satisfied by historic modifiers
  for (const groupId of menuItem.modifierGroupIds ?? []) {
    const group = menu.modifierGroups[groupId];
    if (!group) continue;

    if (group.minRequired > 0) {
      const allModifierIds = new Set([
        ...group.modifiers.map((m) => m.id),
        ...menu.modifiers.filter((m) => m.groupId === groupId).map((m) => m.id),
      ]);
      if (allModifierIds.size === 0) continue;

      const currentModifierIds = new Set([
        ...group.modifiers
          .filter((m) => m.available !== false)
          .map((m) => m.id),
        ...menu.modifiers
          .filter((m) => m.groupId === groupId && m.available !== false)
          .map((m) => m.id),
      ]);
      const satisfiedCount = historicModifiers.filter(
        (m) =>
          (m.modifierGroupId ?? m.groupId) === groupId &&
          currentModifierIds.has(m.id)
      ).length;

      if (satisfiedCount < group.minRequired) {
        return true;
      }
    }
  }

  // Check 2 — a previously chosen modifier no longer exists in its group
  for (const historicMod of historicModifiers) {
    const groupId = historicMod.modifierGroupId ?? historicMod.groupId;
    if (!groupId) continue;

    const group = menu.modifierGroups[groupId];
    if (!group) continue; // group was removed entirely; handled by Check 1 if required

    const stillExists =
      group.modifiers.some(
        (m) => m.id === historicMod.id && m.available !== false
      ) ||
      menu.modifiers.some(
        (m) =>
          m.id === historicMod.id &&
          m.groupId === groupId &&
          m.available !== false
      );
    if (!stillExists) {
      return true;
    }
  }

  return false;
}

/**
 * Classifies a single retail history item given its getItemDetails API response.
 *
 * - null response         → hardUnavailable (item deleted / 404)
 * - available === false   → hardUnavailable
 * - availableForOrdering  checked by caller as an extra gate before reaching here
 * - modifier issue        → needsReAdd
 * - otherwise             → valid
 *
 * The history item ID is looked up directly inside response.items[] for both
 * standard items and variant children.
 */
export function classifyRetailReorderItem(
  historyItem: any,
  details: GetItemDetailsResponse | null
): "valid" | "needsReAdd" | "hardUnavailable" {
  if (!details) return "hardUnavailable";

  // Find the current standard item or exact variant child.
  const matchedItem = getRetailReorderCatalogItem(historyItem, details) ?? null;

  // For variant items, a null matchedItem means the specific variant was deleted.
  // Keep the existing legacy fallback for non-variant 200 responses without items[].
  const isAvailable = matchedItem
    ? matchedItem.available !== false
    : !details.itemWithVariant; // deleted variant → unavailable; non-variant → trust the 200 response

  if (!isAvailable) return "hardUnavailable";

  const modifierGroups = details.modifierGroups ?? {};
  const modifierGroupIds = matchedItem?.modifierGroupIds ?? [];
  const historicModifiers: any[] = historyItem.modifiers ?? [];

  // Check 1 — mandatory groups not satisfied by historic modifiers
  for (const groupId of modifierGroupIds) {
    const group = modifierGroups[groupId];
    if (!group || group.minRequired === 0) continue;

    const currentModifierIds = new Set(group.modifiers.map((m) => m.id));
    const satisfiedCount = historicModifiers.filter(
      (m) =>
        (m.modifierGroupId ?? m.groupId) === groupId &&
        currentModifierIds.has(m.id)
    ).length;

    if (satisfiedCount < group.minRequired) return "needsReAdd";
  }

  // Check 2 — a previously chosen modifier no longer exists in its group
  for (const historicMod of historicModifiers) {
    const groupId = historicMod.modifierGroupId ?? historicMod.groupId;
    if (!groupId) continue;

    const group = modifierGroups[groupId];
    if (!group) continue;

    const stillExists = group.modifiers.some((m) => m.id === historicMod.id);
    if (!stillExists) return "needsReAdd";
  }

  return "valid";
}
